import type { ProtoPlayerBio, ProtoPlayerIndexEntry } from "@catbento/shared";
import type { RawPlayerRecord } from "./snapshot.js";
import { comparePlayersByName, getPlayerShortName } from "./player-utils.js";

// Pure lookups over the snapshot's `players` map. Kept separate from the React
// hooks (usePlayerIndex/usePlayerBio) so the real logic is trivially unit-tested
// without a DOM. They reproduce exactly what the prototype's renderers read.

type Players = Record<string, RawPlayerRecord>;

/**
 * A player, as the app stores and passes her around: her Catalyst DB id (ids everywhere,
 * track2-plan.md §8.6). Never her name — two players can share one, and the API asks by id.
 * Names are for display; the snapshot itself is still keyed by name (it goes away with
 * Track 2), so the snapshot code turns an id back into a name inside (fork A1).
 */
export type PlayerId = number;

/** A snapshot record's id as a PlayerId. Every snapshot player has one (checked 2026-10-06:
 *  1,087 of 1,087, no repeats) — a missing or odd one is a broken snapshot, so it throws. */
export function playerIdOf(record: RawPlayerRecord): PlayerId {
  const id = Number(record.id);
  if (!Number.isInteger(id) || id <= 0) throw new Error(`Snapshot player "${record.name}" has no valid id (${String(record.id)})`);
  return id;
}

// Built once per snapshot object: the picker, the drop rules and every widget ask by id.
const byIdCache = new WeakMap<Players, Map<PlayerId, string>>();

/** id → the snapshot's key (her full name) for every player. */
function nameIndex(players: Players): Map<PlayerId, string> {
  let index = byIdCache.get(players);
  if (!index) {
    index = new Map();
    for (const [name, record] of Object.entries(players)) {
      const id = playerIdOf(record);
      if (index.has(id)) throw new Error(`Snapshot players "${index.get(id)}" and "${name}" share id ${id}`);
      index.set(id, name);
    }
    byIdCache.set(players, index);
  }
  return index;
}

/** The snapshot key (full name) for an id, or null if no snapshot player has it. */
export function playerNameOf(players: Players, id: PlayerId | null): string | null {
  return id === null ? null : (nameIndex(players).get(id) ?? null);
}

/** Does the snapshot have this player? (On `?live=1` a picked player may be anyone in the
 *  real DB — ids-3.) */
export function isSnapshotPlayer(players: Players, id: PlayerId): boolean {
  return nameIndex(players).has(id);
}

/** The snapshot record for an id, or undefined. */
export function playerById(players: Players, id: PlayerId | null): RawPlayerRecord | undefined {
  const name = playerNameOf(players, id);
  return name === null ? undefined : players[name];
}

/** The id of the snapshot player keyed `name` — for code still holding a snapshot name (a
 *  row in a list built from the snapshot). Throws for a name the snapshot doesn't have. */
export function playerIdOfName(players: Players, name: string): PlayerId {
  const record = players[name];
  if (!record) throw new Error(`No snapshot player named "${name}"`);
  return playerIdOf(record);
}

/**
 * The searchable player index — every player, surname-ordered (the app-wide
 * rule, via comparePlayersByName), as the prototype's pickers list them.
 */
export function buildPlayerIndex(players: Players): ProtoPlayerIndexEntry[] {
  const entries: ProtoPlayerIndexEntry[] = [];
  for (const key of Object.keys(players)) {
    const record = players[key];
    if (!record) continue;
    entries.push({
      name: record.name ?? key,
      shortName: getPlayerShortName(record),
      nickname: record.nickname ?? null,
    });
  }
  // Sort by the entry's full name (surname first) — identical to the prototype's
  // `names.sort((a, b) => comparePlayersByName(players[a], players[b]))`.
  entries.sort(comparePlayersByName);
  return entries;
}

/**
 * One player's Bio in the frozen contract shape, or null if the name isn't in
 * the snapshot. Raw fields only (the renderer formats age/HOPS/etc.); missing
 * values become null. hopsRating keeps the prototype's guard: only a numeric
 * `rating` counts, else null (a genuine data gap, e.g. Hannah Hampton).
 */
export function getPlayerBio(players: Players, name: string): ProtoPlayerBio | null {
  const p = players[name];
  if (!p) return null;
  const hops =
    p.hopsRating && typeof p.hopsRating.rating === "number"
      ? { rating: p.hopsRating.rating }
      : null;
  return {
    name: p.name ?? name,
    shortName: getPlayerShortName(p),
    positionTemplate: p.positionTemplate ?? null,
    team: p.team ?? null,
    competition: p.competition ?? null,
    country: p.country ?? null,
    dob: p.dob ?? null,
    height: p.height ?? null,
    weight: p.weight ?? null,
    mostRecentMatchDate: p.mostRecentMatchDate ?? null,
    mostRecentTeam: p.mostRecentTeam ?? null,
    hopsRating: hops,
  };
}

/**
 * The Bio renderer's fall-back player: when a module's requested player isn't in
 * the snapshot, the prototype shows `Object.keys(players)[0]` — the first key in
 * the map's own (insertion) order, NOT the surname-sorted index. Kept faithful to
 * `renderBioModule`'s `players[playerName] ? playerName : names[0]`. `null` only
 * when the snapshot has no players at all (→ the "No player data." empty state).
 */
export function firstPlayerName(players: Players): string | null {
  return Object.keys(players)[0] ?? null;
}
